import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import Seo from "@/components/Seo";
import { Globe, ArrowRight } from "lucide-react";
import { slugify } from "@/lib/slugify";
import { getCountryEditorial } from "@/lib/countryEditorial";

export default function WorldRecipesPage() {
  const [origins, setOrigins] = useState({ countries: [], regions: [] });
  const [recipes, setRecipes] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/recipes/origins").then((r) => setOrigins(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", "48");
    if (selectedCountry) {
      params.set("country", selectedCountry);
    } else {
      params.set("world", "true");
    }
    api.get(`/recipes?${params}`)
      .then((r) => setRecipes(r.data.items || []))
      .finally(() => setLoading(false));
  }, [selectedCountry]);

  const worldCountries = (origins.countries || []).filter((c) => c.name !== "France");
  const totalWorld = worldCountries.reduce((sum, c) => sum + c.count, 0);

  return (
    <>
      <Seo
        title="Cuisines du monde — recettes IG bas pour diabétiques"
        description="Recettes du monde entier adaptées pour personnes diabétiques. Cuisine indienne, italienne, marocaine, japonaise, libanaise… toutes à index glycémique bas."
        keywords={["cuisine du monde diabète", "recettes internationales IG bas", "tajine diabète", "dahl diabétique", "carbonara IG bas", "cuisine internationale diabète"]}
        path="/cuisines-du-monde"
      />

      {/* HERO */}
      <section className="container-x pt-20 pb-12" data-testid="world-hero">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-4 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> Saveurs internationales · IG bas
            </p>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight">Cuisines du monde<br />pour <em className="text-[hsl(var(--brand-primary))] font-medium">diabétiques</em></h1>
            <p className="mt-6 text-lg text-[hsl(var(--text-secondary))] max-w-2xl">
              Le diabète n'a pas de frontières — vos plats préférés non plus.
              Tajines, dahls, carbonara, sushis, gazpacho… toutes les traditions culinaires
              du monde, repensées en index glycémique bas pour respecter votre glycémie.
            </p>
          </div>
          <div className="md:col-span-4 text-right hidden md:block">
            <p className="font-serif text-7xl text-[hsl(var(--brand-primary))]">{totalWorld}</p>
            <p className="overline">recettes internationales</p>
          </div>
        </div>
      </section>

      {/* COUNTRY CARDS - SEO HUB */}
      {worldCountries.length > 0 && (
        <section className="container-x py-12 border-t border-[hsl(var(--border-light))]" data-testid="world-country-hub">
          <p className="overline mb-3">Explorer par pays</p>
          <h2 className="font-serif text-3xl mb-8">Chaque cuisine a sa page IG bas</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="world-country-grid">
            {worldCountries.map((c) => {
              const ed = getCountryEditorial(c.name);
              return (
                <Link
                  key={c.name}
                  to={`/cuisines-du-monde/${slugify(c.name)}`}
                  data-testid={`world-country-card-${slugify(c.name)}`}
                  className="border border-[hsl(var(--border-default))] p-5 bg-white hover:border-[hsl(var(--brand-primary))] transition-colors group"
                >
                  <p className="text-3xl mb-2" aria-hidden>{ed.flag}</p>
                  <p className="font-serif text-2xl group-hover:text-[hsl(var(--brand-primary))] transition-colors">{c.name}</p>
                  <p className="text-xs text-[hsl(var(--text-muted))] mt-1">{c.count} recette{c.count > 1 ? "s" : ""} IG bas</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* QUICK FILTER (in-page) */}
      {worldCountries.length > 0 && (
        <section className="container-x pt-8 pb-2" data-testid="world-country-filter">
          <div className="border-t border-[hsl(var(--border-light))] py-4 flex flex-wrap items-center gap-2">
            <span className="overline mr-2">Filtre rapide</span>
            <button
              onClick={() => setSelectedCountry("")}
              data-testid="world-filter-all"
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors ${
                !selectedCountry
                  ? "bg-[hsl(var(--text-primary))] text-[hsl(var(--bg-main))]"
                  : "text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
              }`}
            >
              Toutes ({totalWorld})
            </button>
            {worldCountries.map((c) => (
              <button
                key={c.name}
                onClick={() => setSelectedCountry(c.name)}
                data-testid={`world-filter-${slugify(c.name)}`}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors ${
                  selectedCountry === c.name
                    ? "bg-[hsl(var(--text-primary))] text-[hsl(var(--bg-main))]"
                    : "text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
                }`}
              >
                {c.name} ({c.count})
              </button>
            ))}
          </div>
        </section>
      )}

      {/* RECIPES GRID */}
      <section className="container-x py-6 pb-24">
        {loading ? (
          <div className="text-[hsl(var(--text-muted))] py-12">Chargement…</div>
        ) : recipes.length === 0 ? (
          <div className="border border-dashed border-[hsl(var(--border-default))] rounded-sm p-16 text-center" data-testid="world-empty">
            <Globe className="w-10 h-10 mx-auto text-[hsl(var(--text-muted))] mb-4" strokeWidth={1.2} />
            <p className="font-serif text-3xl mb-2">Les premières saveurs du monde arrivent</p>
            <p className="text-sm text-[hsl(var(--text-muted))] max-w-md mx-auto">
              Nous ajoutons régulièrement des recettes internationales adaptées en IG bas.
              Revenez bientôt — ou explorez nos recettes du terroir français.
            </p>
            <Link to="/recettes" className="mt-6 inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-primary))] hover:underline">
              Voir toutes les recettes <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {recipes.map((r) => <RecipeCard key={r.id} recipe={r} />)}
          </div>
        )}
      </section>
    </>
  );
}
